export interface PixPaymentResponse {
  success: boolean;
  message?: string;
  data: {
    cre_codigo: string;
    pix: {
      qrcode: string;
      qrcode_text: string;
      status: string;
    };
    invoice_id: string;
    due_date: string;
    secure_url?: string;
  };
}

export interface BoletoPaymentResponse {
  success: boolean;
  message?: string;
  data: {
    cre_codigo: string;
    invoice_id: string;
    bank_slip_url: string;
    bank_slip_pdf_url: string;
    digitable_line: string;
    secure_url: string;
    barcode: string;
    due_date: string;
  };
}

export interface CreditCardPaymentResponse {
  success: boolean;
  data: {
    message: string;
    status: string; // 'authorized' | 'captured' | etc.
    invoice_id: string;
  };
}

export interface CheckPaymentResponse {
  success: boolean;
  data: {
    invoice_id: string;
    pago: boolean;
  };
}

export interface FaturaCotacao {
  cve_codigo: string;
  cre_codigo: string;
  tts_descricao: string;
  empresa: string;
  cre_data_cadastro: string;
  cre_data_emissao: string;
  enc: string;
  cre_data_vencimento: string;
  cre_documento: string | null;
  por_descricao: string;
  cre_valor: string;
  cre_multa: string | null;
  cre_juros: string | null;
  cre_desconto: string | null;
  cre_data_pag: string | null;
  cre_valor_pag: string | null;
  cre_codigo_link: string;
  cre_origem_emissao: string | null;
  cre_url_link: string;
  qrcode: string | null;
  qrcode_text: string | null;
  bank_slip_url: string | null;
  digitable_line: string | null;
  barcode_data: string | null;
  barcode: string | null;
}

export const INVOICE_STATUS = {
  PAGO: "pago",
  A_VENCER: "a vencer",
  AVENCER: "avencer",
  EM_ABERTO: "em aberto",
  VENCIDO: "vencido",
  ESTORNO: "estorno",
  CANCELADO: "cancelado",
} as const;

export type InvoiceStatusKey = keyof typeof INVOICE_STATUS;
export type InvoiceStatusValue = (typeof INVOICE_STATUS)[InvoiceStatusKey];

/** Status que indicam fatura ainda pendente de pagamento */
const UNPAID_STATUSES: string[] = [
  INVOICE_STATUS.A_VENCER,
  INVOICE_STATUS.AVENCER,
  INVOICE_STATUS.EM_ABERTO,
  INVOICE_STATUS.VENCIDO,
];

/** Status que indicam fatura quitada (incluindo estorno como liquidado) */
const PAID_STATUSES: string[] = [
  INVOICE_STATUS.PAGO,
];

/** Status que indicam fatura revertida/cancelada */
const REVERSED_STATUSES: string[] = [
  INVOICE_STATUS.ESTORNO,
  INVOICE_STATUS.CANCELADO,
];

function normalizeStatus(tts_descricao: string): string {
  return tts_descricao?.toLowerCase().trim() ?? "";
}

export function isInvoicePaid(tts_descricao: string): boolean {
  return PAID_STATUSES.includes(normalizeStatus(tts_descricao));
}

export function isInvoiceUnpaid(tts_descricao: string): boolean {
  return UNPAID_STATUSES.includes(normalizeStatus(tts_descricao));
}

export function isInvoiceReversed(tts_descricao: string): boolean {
  return REVERSED_STATUSES.includes(normalizeStatus(tts_descricao));
}

/** Retorna a categoria semântica do status para uso em badges e lógica de UI */
export function getInvoiceStatusCategory(
  tts_descricao: string,
): "paid" | "unpaid" | "overdue" | "reversed" | "unknown" {
  const status = normalizeStatus(tts_descricao);
  if (PAID_STATUSES.includes(status)) return "paid";
  if (REVERSED_STATUSES.includes(status)) return "reversed";
  if (status === INVOICE_STATUS.VENCIDO) return "overdue";
  if (UNPAID_STATUSES.includes(status)) return "unpaid";
  return "unknown";
}
